import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import get from 'lodash.get';

import { AccountSideBarUtils } from '../../utils';
import { AccountInfoContext, USER_ROLES } from '~shared/constants/account-info.constant';
import { AccountMenuItemList } from '../../models/account.model';
import { IAccountInformation } from '~shared/models';
import { AuthService } from '~app/modules/authorization/services/authorization-idam.service';
import { API_ACCOUNT_FLYOUT_ENDPOINT } from '~app/modules/generic/constants/account-flyout.constants';
import { catchError, map } from 'rxjs/operators';
import { AccountContext, AccountFlyoutResponse } from '~app/modules/generic/models/account-flyout-reponse.model';
import { Observable, of } from 'rxjs';
import { AccountService } from '../../services/account.service';
import { ACCOUNT_MENU_GA_EVENT_LABEL, ORDER_HISTORY_MENU } from '../../constants/account.constant';
import { HttpClient } from '@angular/common/http';
import { deserialize } from 'serialize-ts';
import { ActivatedRoute } from '@angular/router';
import { environment } from '~env/environment';
import { AuthorizationHttpService } from '~app/modules/authorization/services/authorization-http.service';
import { isNotSSO, redirectTo } from '~server/utils/auth.utils';


@Component({
  selector: 'app-account-side-bar-menu',
  templateUrl: './account-side-bar-menu.component.html',
  styleUrls: ['./account-side-bar-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSideBarMenuComponent implements OnInit {
  @Input() set accountInfo(info: IAccountInformation) {
    this.profileListing = this.setFlyoutMenu(info);
  }
  @Input() set currentPageUrl(routeUrl: string) {
    this.navigatedUrl = routeUrl;
  }
  @Input() set isPaymentMethodPageVisible(isEnable: boolean) {
    this.isPaymentMethodPageEnable = isEnable;
  }
  @Output() supportClicked = new EventEmitter<void>();

  public profileListing: AccountMenuItemList[];
  public supportItem = AccountSideBarUtils.SUPPORT_ITEM;
  public logoutItem = AccountSideBarUtils.LOGOUT_ITEM;
  private role: string;
  private isPaymentMethodPageEnable: boolean;
  public purchasesLinks: Observable<boolean>;
  public navigatedUrl: string;
  constructor(private cd: ChangeDetectorRef,
    private authService: AuthService,
    private accountService: AccountService,
    private httpClient: HttpClient,
    private activatedRoute: ActivatedRoute,
    private authorizationHttpService: AuthorizationHttpService,
  ) {

  }

  ngOnInit(): void {
    this.purchasesLinks = this.httpClient.get(API_ACCOUNT_FLYOUT_ENDPOINT()).pipe(
      map(response => deserialize(response, AccountFlyoutResponse)),
      map(flyoutData => {
        this.getFlyoutDataSelectedContext(flyoutData);
        return true;
      }),
      catchError(() => of(false))
    );
  }

  private setFlyoutMenu(info: IAccountInformation): AccountMenuItemList[] {
    this.role = get(info, 'role', USER_ROLES.consumer);
    const isTier3 = !!get(info, 'metroCard', null);
    const activeContext = get(info, 'activeContext', AccountInfoContext.personal);
    const menuItems = Object.values(AccountSideBarUtils.getMenuItems(this.role, activeContext, isTier3, true, this.isPaymentMethodPageEnable));
    const isPersonalOrderHistory = this.activatedRoute.snapshot.queryParams.context === AccountInfoContext.personal;

    if (this.role === USER_ROLES.entrepreneur) {
      const personalMenu =
        Object.values(AccountSideBarUtils.getMenuItems(USER_ROLES.consumer, AccountInfoContext.personal, false, true, this.isPaymentMethodPageEnable));
      return [{
        title: this.getOrganisationName(info, USER_ROLES.entrepreneur),
        menuItems,
        role: this.role,
        isPurchasesHidden: isPersonalOrderHistory,
      },
      {
        title: this.getOrganisationName(info, USER_ROLES.consumer),
        menuItems: personalMenu,
        role: USER_ROLES.consumer,
        isPurchasesHidden: !isPersonalOrderHistory,
      }
      ];

    } else {
      return [{
        title: this.getOrganisationName(info),
        menuItems,
        role: this.role,
        isPurchasesHidden: !isPersonalOrderHistory,
      }];
    }
  }

  private getFlyoutDataSelectedContext(flyoutData: AccountFlyoutResponse) {
    const accounts = flyoutData.accounts || [];
    const contexts = [
      {
        id: 'personal',
        label: flyoutData.person.name,
        footer: flyoutData.translations.personalAccount,
        menuEntries: flyoutData.person.menuEntries,
      }
    ].concat(accounts.map(account => ({
      id: account.id,
      label: account.corporateName,
      footer: account.legalAddress,
      menuEntries: account.menuEntries,
      account,
    })));
    this.setPurchasesLinks(flyoutData, contexts);
  }

  public navigateToSupport() {
    this.sendGAEvents(this.supportItem.gaEventLabel);
    this.supportClicked.emit();
  }

  public logout() {
    this.sendGAEvents(this.logoutItem.gaEventLabel);
    this.authService.logout();
  }

  public togglePurchasesContent(item: AccountMenuItemList) {
    item.isPurchasesHidden = !item.isPurchasesHidden;
    this.cd.markForCheck();
  }

  public getOrganisationName(info: IAccountInformation, role?: string): string {
    return role === USER_ROLES.entrepreneur
      ? info.organizationName
      : `${info.firstName} ${info.lastName}`;
  }
  public sendGAEvents(label: string, isExpandableItem?: boolean): void {
    if (isExpandableItem) {
      label = this.getGAEventLabelsOrderHistory(label);
    }
    this.accountService.sendGAEvents(label);
  }

  public handleCSSLinks(event, redirectionURL, label: string, isExpandableItem?: boolean) {
    this.sendGAEvents(label, isExpandableItem);

    if (!redirectionURL) {
      return;
    }

    const urlOrigin = new URL(redirectionURL).origin;
    const myMetroURL = environment.myMetroBaseUrl();

    if (isNotSSO() && urlOrigin === myMetroURL) {
      event.preventDefault();
      this.authorizationHttpService.getLoginLink(redirectionURL).subscribe((res: { loginLink: string }) => {
        if (res?.loginLink) {
          redirectTo(res.loginLink);
        } else {
          redirectTo(redirectionURL);
        }
      });
      return false;
    }
    redirectTo(redirectionURL);
  }

  private getGAEventLabelsOrderHistory(label: string): string {
    if (label === ORDER_HISTORY_MENU.personal) {
      return ACCOUNT_MENU_GA_EVENT_LABEL.personalOrderHistory;
    } else if (label === ORDER_HISTORY_MENU.business) {
      return ACCOUNT_MENU_GA_EVENT_LABEL.businessOrderHistory;
    }
    return label;
  }
  private setPurchasesLinks(flyoutContext: AccountFlyoutResponse, contexts: AccountContext[]) {
    this.profileListing.forEach((profile) => {
      const isEntrepreneur = profile.role === USER_ROLES.entrepreneur;
      profile.purchaseLinks = contexts
        .find((x) => x.id === (isEntrepreneur ? flyoutContext.activeContext : AccountInfoContext.personal))
        .menuEntries.find((x) => x.entries)?.entries;
    });
    this.cd.markForCheck();
  }

}
